import {
  DEFAULT_BINDINGS,
  DEFAULT_GAMEPAD,
  type Action,
  type Bindings,
  type GamepadSettings,
} from '@steelwake/shared/progression';
import type { MechInput } from '@steelwake/shared/sim';

import { AIM_RATE, gamepadActive, readGamepad, type GamepadSample } from './gamepad.js';
import { boundKeys, isDown, mapKeys, mouseCode, RELEASED_KEYS } from './keys.js';

/** True when the browser already lists a connected pad (a page loaded with one plugged in). */
function hasConnectedPad(): boolean {
  if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return false;
  try {
    return navigator.getGamepads().some((p) => p?.connected === true);
  } catch {
    return false;
  }
}

/** Everything in an input packet except the sequence number the session assigns. */
export type InputSample = Omit<MechInput, 'seq'>;

export interface InputHandlers {
  /** Escape (or the bound menu key) pressed: the menu toggles. */
  onEscapeKey(): void;
  /** The browser released the pointer lock (Escape, alt-tab): the menu opens. */
  onPointerLockLost(): void;
  onToggleStats(): void;
  onSwapShoulder(): void;
}

export interface InputOptions {
  initialYaw: number;
  allowPointerLock: boolean;
  sensitivity?: number;
  bindings?: Bindings;
  gamepad?: GamepadSettings;
}

const BASE_SENSITIVITY = 0.0022;
const PITCH_MIN = -1.2;
const PITCH_MAX = 0.9;

/**
 * Keyboard, mouse-button, pointer-lock mouse and gamepad input through the account's bindings
 * (Phase 8). Aim is applied at render rate (DECISIONS.md #16); the axes and buttons are latched
 * into one MechInput per sim tick by the match session. Mouse buttons work without pointer lock
 * in e2e mode. A resting gamepad never overrides the keyboard; a live one adds to it.
 */
export class InputController {
  private readonly pressed = new Set<string>();
  private readonly buttons = new Set<string>();
  private bindings: Bindings;
  private keys: Set<string>;
  private gamepad: GamepadSettings;
  private padPressed = new Set<Action>();
  /** A pad has announced itself; until then `getGamepads()` is never polled (it costs a frame slice). */
  private padSeen = false;
  private scoreboardHeld = false;
  private yaw: number;
  private pitch = 0.22;
  private enabled = true;
  private wasLocked = false;
  private sensitivity = 1;
  private readonly allowPointerLock: boolean;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly handlers: InputHandlers,
    options: InputOptions,
  ) {
    this.yaw = options.initialYaw;
    this.allowPointerLock = options.allowPointerLock;
    this.sensitivity = options.sensitivity ?? 1;
    this.bindings = options.bindings ?? DEFAULT_BINDINGS;
    this.keys = boundKeys(this.bindings);
    this.gamepad = options.gamepad ?? DEFAULT_GAMEPAD;
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    window.addEventListener('gamepadconnected', this.onPadConnected);
    this.padSeen = hasConnectedPad();
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mousedown', this.onMouseDown);
    document.addEventListener('mouseup', this.onMouseUp);
    document.addEventListener('pointerlockchange', this.onLockChange);
    canvas.addEventListener('click', this.onClick);
    canvas.addEventListener('contextmenu', this.onContextMenu);
  }

  get pointerLocked(): boolean {
    return document.pointerLockElement === this.canvas;
  }

  /** The scoreboard key (or pad button) is held. */
  get scoreboard(): boolean {
    return this.scoreboardHeld;
  }

  /** Any key or button is down (the kill cam skips on this). */
  get anyPressed(): boolean {
    return this.pressed.size > 0 || this.buttons.size > 0 || this.padPressed.size > 0;
  }

  /** Takes the pointer lock from a user gesture outside the canvas (the Deploy click). */
  requestPointerLock(): void {
    this.onClick();
  }

  /** One sample per rendered frame; `dt` scales the gamepad's aim rate. */
  sample(dt = 0): InputSample {
    const down = new Set(this.pressed);
    if (this.buttonsActive()) for (const b of this.buttons) down.add(b);
    const axes = this.enabled ? mapKeys(down, this.bindings) : RELEASED_KEYS;
    let { moveX, moveY, boost, jump, ability, ultimate, vent, melee } = axes;
    let primary = this.enabled && isDown(down, this.bindings, 'primary');
    let secondary = this.enabled && isDown(down, this.bindings, 'secondary');
    const pad = this.enabled && this.gamepad.enabled && this.padSeen ? this.pollGamepad() : null;
    if (pad && gamepadActive(pad)) {
      if (moveX === 0 && moveY === 0) {
        moveX = pad.moveX;
        moveY = pad.moveY;
      }
      const k = AIM_RATE * this.gamepad.sensitivity * dt;
      this.yaw -= pad.aimX * k;
      this.pitch = Math.min(PITCH_MAX, Math.max(PITCH_MIN, this.pitch + pad.aimY * k));
      boost ||= pad.pressed.has('boost');
      jump ||= pad.pressed.has('jump');
      ability ||= pad.pressed.has('ability');
      ultimate ||= pad.pressed.has('ultimate');
      vent ||= pad.pressed.has('vent');
      melee ||= pad.pressed.has('melee');
      primary ||= pad.pressed.has('primary');
      secondary ||= pad.pressed.has('secondary');
    }
    return {
      moveX,
      moveY,
      boost,
      jump,
      ability,
      ultimate,
      vent,
      melee,
      primary,
      secondary,
      aimYaw: this.yaw,
      aimPitch: this.pitch,
    };
  }

  /** Menu open: ignore movement keys and buttons, and release the pointer. */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.pressed.clear();
      this.buttons.clear();
      this.scoreboardHeld = false;
      if (this.pointerLocked) document.exitPointerLock();
    }
  }

  setSensitivity(multiplier: number): void {
    this.sensitivity = multiplier;
  }

  setBindings(bindings: Bindings): void {
    this.bindings = bindings;
    this.keys = boundKeys(bindings);
  }

  setGamepad(settings: GamepadSettings): void {
    this.gamepad = settings;
  }

  /** Scripted aim for the autopilot, the debug API and tests. */
  setAim(yaw: number, pitch: number): void {
    this.yaw = yaw;
    this.pitch = Math.min(PITCH_MAX, Math.max(PITCH_MIN, pitch));
  }

  get aim(): { yaw: number; pitch: number } {
    return { yaw: this.yaw, pitch: this.pitch };
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    window.removeEventListener('gamepadconnected', this.onPadConnected);
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mousedown', this.onMouseDown);
    document.removeEventListener('mouseup', this.onMouseUp);
    document.removeEventListener('pointerlockchange', this.onLockChange);
    this.canvas.removeEventListener('click', this.onClick);
    this.canvas.removeEventListener('contextmenu', this.onContextMenu);
    if (this.pointerLocked) document.exitPointerLock();
  }

  /** Reads the first connected pad; menu, swap and scoreboard buttons are edge-triggered here. */
  private pollGamepad(): GamepadSample | null {
    if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') {
      return null;
    }
    let pad: Gamepad | null = null;
    for (const candidate of navigator.getGamepads()) {
      if (candidate?.connected) {
        pad = candidate;
        break;
      }
    }
    if (!pad) {
      this.padPressed = new Set();
      return null;
    }
    const sample = readGamepad(pad, this.gamepad);
    const rose = (action: Action): boolean =>
      sample.pressed.has(action) && !this.padPressed.has(action);
    if (rose('menu')) this.handlers.onEscapeKey();
    if (rose('swapShoulder')) this.handlers.onSwapShoulder();
    this.scoreboardHeld =
      sample.pressed.has('scoreboard') ||
      [...this.pressed].some((code) => this.bindings.scoreboard.includes(code));
    this.padPressed = sample.pressed;
    return sample;
  }

  private bound(code: string, action: Action): boolean {
    return this.bindings[action].includes(code);
  }

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    // Escape always reaches the menu, whatever the bindings say (nobody gets locked in).
    if (e.code === 'Escape' || this.bound(e.code, 'menu')) {
      this.handlers.onEscapeKey();
      return;
    }
    if (e.repeat) return;
    if (this.bound(e.code, 'stats')) {
      e.preventDefault();
      this.handlers.onToggleStats();
      return;
    }
    if (this.bound(e.code, 'swapShoulder') && this.enabled) {
      this.handlers.onSwapShoulder();
      return;
    }
    if (this.bound(e.code, 'scoreboard')) {
      e.preventDefault();
      if (this.enabled) this.scoreboardHeld = true;
      return;
    }
    if (this.keys.has(e.code)) {
      e.preventDefault();
      if (this.enabled) this.pressed.add(e.code);
    }
  };

  private readonly onKeyUp = (e: KeyboardEvent): void => {
    this.pressed.delete(e.code);
    if (this.bound(e.code, 'scoreboard')) this.scoreboardHeld = false;
  };

  private readonly onPadConnected = (): void => {
    this.padSeen = true;
  };

  private readonly onBlur = (): void => {
    this.pressed.clear();
    this.buttons.clear();
    this.scoreboardHeld = false;
  };

  private readonly onMouseMove = (e: MouseEvent): void => {
    if (!this.enabled || !this.pointerLocked) return;
    const k = BASE_SENSITIVITY * this.sensitivity;
    this.yaw -= e.movementX * k;
    this.pitch = Math.min(PITCH_MAX, Math.max(PITCH_MIN, this.pitch + e.movementY * k));
  };

  /** Buttons count while the pointer is locked, or always in e2e mode (no lock available). */
  private buttonsActive(): boolean {
    return this.enabled && (this.pointerLocked || !this.allowPointerLock);
  }

  private readonly onMouseDown = (e: MouseEvent): void => {
    this.buttons.add(mouseCode(e.button));
  };

  private readonly onMouseUp = (e: MouseEvent): void => {
    this.buttons.delete(mouseCode(e.button));
  };

  private readonly onContextMenu = (e: Event): void => {
    e.preventDefault();
  };

  private readonly onLockChange = (): void => {
    const locked = this.pointerLocked;
    if (this.wasLocked && !locked && this.enabled) this.handlers.onPointerLockLost();
    this.wasLocked = locked;
  };

  private readonly onClick = (): void => {
    if (!this.enabled || !this.allowPointerLock || this.pointerLocked) return;
    // Only from a user gesture, and the promise is swallowed: a refused lock must not surface as
    // an unhandled rejection (the console gate treats that as an error).
    try {
      const result = this.canvas.requestPointerLock() as unknown;
      if (result instanceof Promise) result.catch(() => undefined);
    } catch {
      // Pointer lock unavailable (embedded views); keyboard still works.
    }
  };
}
